/* Hydrofoil Academy — demo line.
 *
 * The transport is the Pipecat JS SDK over Daily WebRTC. The previous version of this
 * file spoke SmallWebRTC signalling by hand to avoid a bundler; what that bought in
 * simplicity it lost in everything the SDK does that a hand-rolled peer connection
 * does not — renegotiation, trickle ICE, reconnection, and a TURN relay for callers
 * whose network will not carry a direct connection.
 *
 * The protocol, in full:
 *   POST /api/config             -> {passcodeRequired, maxCallSeconds}
 *   POST /api/session {passcode} -> {token}
 *   POST /api/connect {token}    -> {dailyRoom, dailyToken, sessionId}
 *   client.connect(that)         -- DailyTransport maps the keys itself.
 *
 * Read the audio section before touching anything that plays sound. Echo cancellation
 * depends on where the bot's voice is routed, and it fails silently.
 */

import { PipecatClient } from "@pipecat-ai/client-js";
import { DailyTransport } from "@pipecat-ai/daily-transport";

(() => {
  "use strict";

  const $ = (id) => document.getElementById(id);

  const el = {
    screens: {
      gate: $("screen-gate"),
      dialer: $("screen-dialer"),
      call: $("screen-call"),
      ended: $("screen-ended"),
    },
    phoneScreen: $("phone-screen"),
    islandTimer: $("island-timer"),
    gateForm: $("gate-form"),
    passcode: $("passcode"),
    gateError: $("gate-error"),
    dialError: $("dial-error"),
    callButton: $("call-button"),
    liveDisc: $("live-disc"),
    wave: $("wave"),
    callState: $("call-state"),
    callTimer: $("call-timer"),
    speakingLabel: $("speaking-label"),
    captions: $("captions"),
    muteButton: $("mute-button"),
    muteLabel: $("mute-label"),
    endButton: $("end-button"),
    limitNote: $("limit-note"),
    ringNote: $("ring-note"),
    stepper: $("stepper"),
    readoutBody: $("readout-body"),
    endedEyebrow: $("ended-eyebrow"),
    endedTitle: $("ended-title"),
    endedSub: $("ended-sub"),
    cardWrap: $("card-wrap"),
    leadSheet: $("lead-sheet"),
    copyButton: $("copy-button"),
    remoteAudio: $("remote-audio"),
  };

  // The caller's words, not the funnel's. "Routed" and "Closed" are how the code
  // thinks about a call; nobody ringing a boatyard has ever wanted to be routed.
  const STEPS = [
    ["connected", "Answered"],
    ["routed", "What you're after"],
    ["understood", "You and your group"],
    ["offered", "What's free"],
    ["agreed", "Locked in"],
    ["closed", "What happens next"],
  ];

  let config = { passcodeRequired: false, maxCallSeconds: 300 };
  let lastState = null;

  /* THE TOKEN IS MINTED WHEN THE CALL IS PLACED, not when the page loads - see createToken()
   * below. `passcode` is the code the visitor typed at the gate and the server accepted; it
   * lives here, in this closure, for as long as the tab is open and NOWHERE else - no
   * storage, no cookie, no URL - because every re-mint presents it to /api/session again,
   * which checks it again. That is stronger than what it replaces: changing DEMO_PASSCODE
   * now stops an open tab calling within one token's life, instead of it having been asked
   * for once, an hour ago, and never since. */
  let passcode = "";

  // Live call. Held at module scope so nothing here is garbage collected mid-call —
  // in particular remoteSource, which is what keeps the WebAudio graph alive.
  let client = null;
  let audioCtx = null;
  let remoteSource = null;
  let remoteAnalyser = null;
  let micSource = null;
  let micAnalyser = null;
  let timerTimer = null;
  let rafId = null;
  let startedAt = 0;
  let ended = false;
  let isConnected = false;
  let returnTimer = null;
  let ringNoteTimers = [];
  let callT0 = 0;

  /* ── screens ────────────────────────────────────────────────────── */

  function show(name) {
    for (const [key, node] of Object.entries(el.screens)) node.hidden = key !== name;
  }

  function fail(node, message) {
    node.textContent = message;
    node.hidden = false;
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res;
  }

  /* ── the token ───────────────────────────────────────────────────────
   * A demo link is read, left open in a tab, and pressed much later. The token
   * /api/connect wants lives ten minutes (functions/api/_auth.js) and that short life is
   * the point: nothing can revoke one once it is minted, and ten minutes is all a leaked
   * one is worth. So it is minted a moment before it is SPENT rather than on the way in.
   * Minting it at page load is what made an hour-old page answer the call button with
   * "that session expired", and answer the second press with a reload notice.
   *
   * The life of a token is the `expiresIn` the mint reported: a DURATION, counted from the
   * moment the answer landed on this clock - never the `exp` inside the token, which is the
   * server's clock. The margin comes off the end so a call placed on a token's last second
   * is not a call that reaches the server a second too late. */
  const TOKEN_MARGIN_MS = 30000;
  const TOKEN_FALLBACK_SECONDS = 60;

  function createToken(mint, marginMs = TOKEN_MARGIN_MS) {
    let token = null;
    let goodUntil = 0;
    let minting = null;

    async function fetchOne() {
      token = null;
      goodUntil = 0;
      const res = await mint().catch(() => null);
      if (!res) return { ok: false, status: 0 };
      if (!res.ok) return { ok: false, status: res.status };
      const body = await res.json().catch(() => ({}));
      if (!body.token) return { ok: false, status: res.status };
      token = body.token;
      // A mint that forgot to say costs a fresh token per press, which is the safe way to
      // be wrong: the call still goes through.
      const seconds = Number(body.expiresIn) > 0 ? Number(body.expiresIn) : TOKEN_FALLBACK_SECONDS;
      goodUntil = Date.now() + seconds * 1000;
      return { ok: true, status: res.status, token };
    }

    return {
      get() {
        if (token && Date.now() < goodUntil - marginMs) {
          return Promise.resolve({ ok: true, status: 200, token });
        }
        // One mint for two presses in the same second.
        if (!minting) minting = fetchOne().finally(() => { minting = null; });
        return minting;
      },
      clear() {
        token = null;
        goodUntil = 0;
      },
    };
  }

  // Created HERE, below TOKEN_MARGIN_MS, and not up beside `passcode`: createToken's default
  // margin reads that const, and calling it any earlier in the module throws "Cannot access
  // before initialization" at page load - which leaves the phone blank and the page dead.
  const sessionToken = createToken(() => postJson("/api/session", { passcode }));

  /* ── the audio context ──────────────────────────────────────────────
   * Opened IN THE TAP: placeCall() calls this, and remoteAudio.play(), before its first
   * await, and resume() is never awaited. A phone lets a page start sound only while the
   * tap that asked for it is still live, and two fetches later it is not. Chrome on iOS
   * then leaves the context suspended with a resume() that never settles, so an awaited
   * one hangs the call on "calling…" before Daily ever asks for the microphone. An
   * Android in-app browser refuses the late play() instead, and the bot is silent.
   * A browser that cannot make one still gets a call: the ringback and the analysers
   * all take a null context as "no ringback, no waveform". */

  function openAudioContext() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctx.resume().catch(() => {});
      return ctx;
    } catch (err) {
      console.warn("no audio context; the call goes ahead without ringback or waveform", err);
      return null;
    }
  }

  /* ── ringback ───────────────────────────────────────────────────────
   * Australian cadence: 400 Hz modulated at 25 Hz, 0.4s on / 0.2s off /
   * 0.4s on / 2.0s off. The disc's sonar rings run the same 3s cycle in CSS, so
   * what you hear and what you see are one event.
   *
   * This is the one thing that does go to audioCtx.destination, and it is safe to:
   * it is silenced before the bot's track arrives, so it never overlaps the window
   * in which the browser needs a clean echo-cancellation reference. */

  const RING_CYCLE = 3.0;
  const RING_LEVEL = 0.14;
  let ringNodes = null;
  let ringTimer = null;

  function startRingback() {
    if (!audioCtx || ringNodes) return;
    const cadence = audioCtx.createGain();
    cadence.gain.value = 0;
    cadence.connect(audioCtx.destination);

    const am = audioCtx.createGain();
    am.gain.value = 0.5;
    am.connect(cadence);

    const carrier = audioCtx.createOscillator();
    carrier.frequency.value = 400;
    carrier.connect(am);

    const mod = audioCtx.createOscillator();
    mod.frequency.value = 25;
    const depth = audioCtx.createGain();
    depth.gain.value = 0.5;
    mod.connect(depth);
    depth.connect(am.gain);

    carrier.start();
    mod.start();
    ringNodes = { cadence, carrier, mod };

    const cycle = (t0) => {
      const g = cadence.gain;
      g.setValueAtTime(0, t0);
      g.linearRampToValueAtTime(RING_LEVEL, t0 + 0.02);
      g.setValueAtTime(RING_LEVEL, t0 + 0.38);
      g.linearRampToValueAtTime(0, t0 + 0.4);
      g.setValueAtTime(0, t0 + 0.6);
      g.linearRampToValueAtTime(RING_LEVEL, t0 + 0.62);
      g.setValueAtTime(RING_LEVEL, t0 + 0.98);
      g.linearRampToValueAtTime(0, t0 + 1.0);
    };
    cycle(audioCtx.currentTime + 0.05);
    ringTimer = setInterval(() => {
      if (ringNodes) cycle(audioCtx.currentTime + 0.05);
    }, RING_CYCLE * 1000);
  }

  function stopRingback() {
    clearInterval(ringTimer);
    ringTimer = null;
    if (!ringNodes) return;
    const { cadence, carrier, mod } = ringNodes;
    ringNodes = null;
    const now = audioCtx.currentTime;
    cadence.gain.cancelScheduledValues(now);
    cadence.gain.setValueAtTime(cadence.gain.value, now);
    cadence.gain.linearRampToValueAtTime(0, now + 0.08);
    setTimeout(() => {
      try { carrier.stop(); mod.stop(); } catch (_) { /* already stopped */ }
    }, 150);
  }

  /* ── the waveform ───────────────────────────────────────────────── */

  function startWave() {
    const ctx2d = el.wave.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = 440;
    el.wave.width = size * dpr;
    el.wave.height = size * dpr;
    ctx2d.scale(dpr, dpr);

    const BARS = 68;
    // One buffer per source. Sharing a single array meant whichever analyser was
    // read last decided the shape, so the bars always drew the microphone.
    const agentBins = new Uint8Array(256);
    const callerBins = new Uint8Array(256);
    const smoothed = new Float32Array(BARS);

    const read = (analyser, into) => {
      if (!analyser) {
        into.fill(0);
        return 0;
      }
      analyser.getByteFrequencyData(into);
      let sum = 0;
      for (let i = 0; i < 64; i++) sum += into[i];
      return sum / 64 / 255;
    };

    const cx = size / 2;
    const cy = size / 2;
    const base = size * 0.305;

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      ctx2d.clearRect(0, 0, size, size);

      const agent = read(remoteAnalyser, agentBins);
      const caller = read(micAnalyser, callerBins);
      const agentLouder = agent >= caller;
      const bins = agentLouder ? agentBins : callerBins;

      // Leaf green from the wordmark when the agent talks, white when you do.
      ctx2d.strokeStyle = agentLouder ? "#8cc63f" : "#f2f6f8";
      ctx2d.lineCap = "round";
      ctx2d.lineWidth = 9;

      for (let i = 0; i < BARS; i++) {
        const idx = Math.floor((i / BARS) * 56);
        const target = bins[idx] / 255;
        smoothed[i] += (target - smoothed[i]) * 0.3;
        const len = 5 + smoothed[i] * size * 0.15;
        const a = (i / BARS) * Math.PI * 2 - Math.PI / 2;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        ctx2d.globalAlpha = 0.3 + smoothed[i] * 0.7;
        ctx2d.beginPath();
        ctx2d.moveTo(cx + cos * base, cy + sin * base);
        ctx2d.lineTo(cx + cos * (base + len), cy + sin * (base + len));
        ctx2d.stroke();
      }
      ctx2d.globalAlpha = 1;
    };
    draw();
  }

  function stopWave() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  }

  /* ── captions ───────────────────────────────────────────────────── */

  let callerLive = null;
  let agentLive = null;
  let agentBuffer = "";

  function caption(who, cls) {
    const node = document.createElement("p");
    node.className = `caption ${cls}`;
    node.dataset.who = who;
    el.captions.appendChild(node);
    while (el.captions.children.length > 8) el.captions.firstElementChild.remove();
    for (const child of el.captions.children) child.classList.add("caption-fading");
    node.classList.remove("caption-fading");
    el.captions.scrollTop = el.captions.scrollHeight;
    return node;
  }

  /* ── the read-out ───────────────────────────────────────────────── */

  const num = (v) => (v === null || v === undefined ? null : String(Number(v.toFixed(2))));
  const money = (v) => (v === null || v === undefined ? null : `$${Number(v).toLocaleString("en-AU")}`);
  const pretty = (v) => (v ? String(v).replace(/_/g, " ") : null);

  // Past about this length a value can no longer sit opposite its label in half a
  // column without turning into a ragged right-aligned paragraph; it goes underneath.
  const STACK_AT = 34;

  function rowsFrom(pairs) {
    return pairs
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([label, value, strong]) => {
        const row = document.createElement("div");
        row.className = String(value).length > STACK_AT ? "row row-stack" : "row";
        const l = document.createElement("span");
        l.className = "row-label";
        l.textContent = label;
        const v = document.createElement("span");
        v.className = strong ? "row-value row-value-strong" : "row-value";
        v.textContent = value;
        row.append(l, v);
        return row;
      });
  }

  function group(title, rows, wide) {
    if (!rows.length) return null;
    const wrap = document.createElement("section");
    wrap.className = wide ? "readout-group readout-group-wide" : "readout-group";
    const h = document.createElement("h3");
    h.className = "group-title";
    h.textContent = title;
    wrap.append(h, ...rows);
    return wrap;
  }

  // Shared by the live read-out and the finished sheet, so the two can't disagree
  // about the same caller.
  const SITE = { rose_bay: "Rose Bay", narrabeen: "Narrabeen" };
  const DISCIPLINE = {
    tow: "tow foiling", wing: "wing foiling", foil_assist: "foil assist",
    prone: "prone foiling", downwind: "downwind",
  };

  const wantedText = (w) => {
    const bits = [DISCIPLINE[w.discipline] || pretty(w.discipline), SITE[w.site]];
    return bits.filter(Boolean).join(" at ") || null;
  };

  const partyText = (w) =>
    w.partySize && w.partySize > 1 ? `${w.partySize} people` : null;

  const setupText = (z) =>
    z ? `${z.boardLitres} L board · ${z.frontWingCm2} cm² ${z.aspect}-aspect · ${z.wingM2} m² wing` : null;

  function renderStepper(current) {
    const at = STEPS.findIndex(([key]) => key === current);
    el.stepper.replaceChildren(
      ...STEPS.map(([, label], i) => {
        const li = document.createElement("li");
        li.className = `step${i < at ? " step-done" : ""}${i === at ? " step-now" : ""}`;
        li.textContent = label;
        return li;
      })
    );
  }

  function renderReadout(state) {
    lastState = state;
    renderStepper(state.step);

    const parts = [];

    if (state.safetyIncident) {
      const alert = document.createElement("p");
      alert.className = "alert-row";
      alert.textContent = "Battery safety incident reported";
      parts.push(alert);
    }
    if (state.humanRequested) {
      const alert = document.createElement("p");
      alert.className = "alert-row";
      alert.textContent = "Asked for a person";
      parts.push(alert);
    }

    const c = state.caller || {};
    const w = state.wanted || {};
    const r = state.rider || {};

    parts.push(
      group("Caller", rowsFrom([
        ["Name", c.name],
        ["Mobile", c.mobile],
        ["Suburb", c.suburb],
        ["Experience", pretty(r.experience)],
        ["Weight", r.weightKg ? `${num(r.weightKg)} kg` : null],
      ]))
    );

    parts.push(
      group("After", rowsFrom([
        ["Session", wantedText(w)],
        ["Group", partyText(w)],
        ["When", w.when],
      ]))
    );

    // The line that explains the whole product to whoever is watching the demo: they
    // asked for one thing and are being offered another, for a reason.
    if (state.conditions) {
      parts.push(group("Conditions", rowsFrom([["Why not", state.conditions]]), true));
    }

    if (state.agreed) {
      const rows = rowsFrom([
        ["Session", state.agreed.spoken, true],
        ["Item", state.agreed.item],
      ]);
      parts.push(group("Locked in", rows, true));
    } else if ((state.offered || []).length) {
      const rows = state.offered.map((o) => {
        const row = document.createElement("div");
        row.className = "row row-stack";
        const l = document.createElement("span");
        l.className = "row-label";
        l.textContent = o.status === "phone_only" ? "Phone only" : "Free";
        const v = document.createElement("span");
        v.className = "row-value";
        v.textContent = o.spoken;
        row.append(l, v);
        return row;
      });
      parts.push(group("What's free", rows, true));
    }

    if (state.sizing) {
      const rows = rowsFrom([
        ["Setup", setupText(state.sizing)],
        ["Mast", state.sizing.mastCm ? `${num(state.sizing.mastCm)} cm` : null],
        ["Complete", money(state.sizing.totalAud), true],
      ]);
      parts.push(group("Sized", rows, true));
    }

    if ((state.products || []).length) {
      const list = document.createElement("ul");
      list.className = "s-list";
      for (const item of state.products) {
        const li = document.createElement("li");
        const brand = document.createElement("span");
        brand.className = "s-brand";
        brand.textContent = item.name;
        li.append(brand);
        if (item.price) {
          const price = document.createElement("span");
          price.className = "s-price";
          price.textContent = money(item.price);
          li.append(price);
        }
        list.append(li);
      }
      parts.push(group("Gear", [list], true));
    }

    if ((state.quoted || []).length) {
      parts.push(group("Price", rowsFrom(
        state.quoted.map((q) => ["Quoted", q])
      ), true));
    }

    if (state.staleData) {
      const note = document.createElement("p");
      note.className = "alert-row alert-row-quiet";
      note.textContent = "Times came from the snapshot — hedged, and need confirming";
      parts.push(group("Note", [note], true));
    }

    el.readoutBody.replaceChildren(...parts.filter(Boolean));
  }

  /* ── the finished lead ──────────────────────────────────────────────
   * The same facts as the read-out above it, in the order somebody who has to ring
   * this person back actually reads them: who they are, the three things you check
   * before you dial, then the one thing to do. The plain-text card is unchanged and
   * is still what the Copy button hands over — that shape is right for a message,
   * it was only ever wrong for a page.
   */

  // A third of the panel is a narrow column, so a value stacks under its label much
  // sooner here than it does in the read-out.
  const SHEET_STACK_AT = 20;

  function sheetRow(label, value, strong) {
    if (value === null || value === undefined || value === "") return null;
    const row = document.createElement("div");
    row.className =
      String(value).length > SHEET_STACK_AT ? "s-row s-row-stack" : "s-row";
    const l = document.createElement("span");
    l.className = "s-label";
    l.textContent = label;
    const val = document.createElement("span");
    val.className = strong ? "s-value s-value-strong" : "s-value";
    val.textContent = value;
    row.append(l, val);
    return row;
  }

  /** One column, made of [title, nodes] blocks; empty blocks disappear entirely. */
  function sheetColumn(blocks) {
    const kept = [];
    for (const [title, nodes] of blocks) {
      const rows = nodes.filter(Boolean);
      if (!rows.length) continue;
      const h = document.createElement("h4");
      h.className = kept.length ? "sheet-title sheet-title-gap" : "sheet-title";
      h.textContent = title;
      kept.push(h, ...rows);
    }
    if (!kept.length) return null;
    const col = document.createElement("div");
    col.className = "sheet-sec";
    col.append(...kept);
    return col;
  }

  function productList(products) {
    if (!products.length) return [];
    const list = document.createElement("ul");
    list.className = "s-list";
    for (const p of products) {
      const li = document.createElement("li");
      const brand = document.createElement("span");
      brand.className = "s-brand";
      brand.textContent = `${p.brand} ${p.model}`;
      li.append(brand);
      if (p.price) {
        const price = document.createElement("span");
        price.className = "s-price";
        price.textContent = `from ${money(p.price)}`;
        li.append(price);
      }
      list.append(li);
    }
    return [list];
  }

  function renderLeadSheet(state) {
    const c = state.caller || {};
    const r = state.rider || {};
    const w = state.wanted || {};
    const na = state.nextAction || {};

    // Who, and how urgently.
    const head = document.createElement("div");
    head.className = "sheet-head";

    const who = document.createElement("div");
    const id = document.createElement("p");
    id.className = "sheet-id";
    id.textContent = [state.callId, SITE[w.site]].filter(Boolean).join(" · ");
    const name = document.createElement("p");
    name.className = "sheet-name";
    name.textContent = c.name || "Name not given";
    const meta = document.createElement("p");
    meta.className = "sheet-meta";
    meta.textContent = [
      c.mobile || "no number",
      c.suburb,
      pretty(state.lane) === "unknown" ? null : pretty(state.lane),
    ].filter(Boolean).join(" · ");
    who.append(id, name, meta);
    head.append(who);

    const alertRows = [];
    if (state.safetyIncident) {
      const alert = document.createElement("p");
      alert.className = "alert-row";
      alert.textContent = "Battery safety incident — escalated";
      alertRows.push(alert);
    }
    if (state.humanRequested) {
      const alert = document.createElement("p");
      alert.className = "alert-row";
      alert.textContent = "Asked for a person";
      alertRows.push(alert);
    }

    const wanted = sheetColumn([
      ["What they wanted", [
        sheetRow("Session", wantedText(w)),
        sheetRow("Group", partyText(w)),
        sheetRow("When", w.when),
        sheetRow("Experience", pretty(r.experience)),
        sheetRow("Weight", r.weightKg ? `${num(r.weightKg)} kg` : null),
      ]],
      // The reason a human reading this later would otherwise not understand why the
      // caller ended up where they did.
      ["Conditions", [sheetRow("Why not", state.conditions)]],
    ]);

    const outcome = sheetColumn([
      ["What they took", [
        sheetRow("Session", state.agreed ? state.agreed.spoken : null, true),
        sheetRow("Item", state.agreed ? state.agreed.item : null),
      ]],
      ["Sized", [
        sheetRow("Setup", setupText(state.sizing)),
        sheetRow("Complete", state.sizing ? money(state.sizing.totalAud) : null, true),
      ]],
      ["Quoted", (state.quoted || []).map((q) => sheetRow("Price", q))],
    ]);

    const cols = document.createElement("div");
    cols.className = "sheet-cols";
    cols.append(...[wanted, outcome].filter(Boolean));

    let action = null;
    if (na.kind) {
      action = document.createElement("div");
      action.className = "sheet-action";
      const h = document.createElement("h4");
      h.className = "sheet-title";
      h.textContent = "Next";
      action.append(h);
      action.append(...rowsFrom([
        ["Do", `${pretty(na.kind)}${na.detail ? " — " + na.detail : ""}`, true],
      ]));
    }

    let why = null;
    if ((state.notes || []).length) {
      why = document.createElement("div");
      why.className = "sheet-why";
      const h = document.createElement("h4");
      h.className = "sheet-title";
      h.textContent = "Because";
      const list = document.createElement("ul");
      list.className = "s-list";
      for (const line of state.notes || []) {
        const li = document.createElement("li");
        li.textContent = line;
        list.append(li);
      }
      why.append(h, list);
    }

    el.leadSheet.replaceChildren(...[head, ...alertRows, cols, action, why].filter(Boolean));
  }

  /* ── audio ──────────────────────────────────────────────────────────
   * Echo cancellation lives or dies here, so the rule is worth stating plainly.
   *
   * The bot's voice MUST be played through the <audio> element. That is the path the
   * browser's AEC uses as its reference signal when it subtracts what the speakers
   * are playing from what the microphone hears. Route it through
   * audioCtx.destination instead and Chrome's AEC flatlines: the agent hears itself,
   * transcribes itself, and interrupts itself.
   *
   * The analysers below are a TAP for the waveform. They are deliberately never
   * connected onward to audioCtx.destination — a MediaStreamSource with nothing
   * downstream still pulls data for getByteFrequencyData, so the visualiser works
   * without making a second, AEC-invisible playback path.
   *
   * The transport does not play anything itself (there is no srcObject or .play() in
   * @pipecat-ai/daily-transport), so this is the only audio output in the app. */

  function attachBotAudio(track) {
    const stream = new MediaStream([track]);
    el.remoteAudio.srcObject = stream;
    el.remoteAudio.play().catch(() => {});

    if (!audioCtx) return;
    // Chrome only routes a WebRTC stream into WebAudio when it is also attached to a
    // media element, so both the element above and this source node are required.
    remoteSource = audioCtx.createMediaStreamSource(stream);
    remoteAnalyser = audioCtx.createAnalyser();
    remoteAnalyser.fftSize = 512;
    remoteSource.connect(remoteAnalyser);
  }

  function attachMicAnalyser(track) {
    if (!audioCtx || micAnalyser) return;
    // The transport's own capture, tapped. Calling getUserMedia again here would open
    // a second microphone stream, which is exactly how AEC gets defeated.
    micSource = audioCtx.createMediaStreamSource(new MediaStream([track]));
    micAnalyser = audioCtx.createAnalyser();
    micAnalyser.fftSize = 512;
    micSource.connect(micAnalyser);
  }

  /** Report what the browser actually applied, since constraints are requests. */
  function reportAudioConstraints() {
    const track = client?.tracks()?.local?.audio;
    if (!track || typeof track.getSettings !== "function") return;
    const s = track.getSettings();
    if (s.echoCancellation === false) {
      console.warn("Echo cancellation is OFF for this microphone — expect the agent " +
                   "to hear itself. Check chrome://webrtc-internals.", s);
    } else {
      console.info("mic:", {
        echoCancellation: s.echoCancellation,
        noiseSuppression: s.noiseSuppression,
        autoGainControl: s.autoGainControl,
      });
    }
  }

  /* ── the call ───────────────────────────────────────────────────── */

  function setCallState(text) {
    el.callState.textContent = text;
  }

  /* The cold-start script. A Pipecat Cloud container that is not already warm takes
   * ten to twenty seconds to come up, and a ringing screen that says nothing for that
   * long reads as a hang - people hang up before the agent ever picks up. One line at
   * four seconds was not enough: it is the stretch after it that feels long. So the
   * note is a short script, in the order the agent actually boots (settings, voice,
   * greeting, then the turn-detection models, then it joins the room), and it only turns
   * to "try again" once the wait is genuinely abnormal.
   *
   * Timed from the tap, not from /api/connect returning: the cold start is usually inside
   * that request, and a note armed after it would miss the very wait it is there for.
   * A warm call connects before the first line fires, so it never shows any of this. */
  const RING_NOTES = [
    [3, "Waking the line — the first call takes about ten seconds."],
    [8, "Starting the agent…"],
    [13, "Loading its voice…"],
    [18, "Nearly there — when it picks up, just talk."],
    [28, "Taking longer than usual. Hang on a moment."],
    [45, "Still not through. Hang up and try again."],
  ];

  function showRingNote(text) {
    el.ringNote.textContent = text;
    el.ringNote.hidden = false;
    // A line replacing another should fade up the way the first one did. Unhiding runs
    // the animation once; a text swap alone would not.
    el.ringNote.style.animation = "none";
    void el.ringNote.offsetWidth;
    el.ringNote.style.animation = "";
  }

  function scheduleRingNotes() {
    clearRingNote();
    const elapsed = performance.now() - callT0;
    for (const [at, text] of RING_NOTES) {
      const delay = Math.max(0, at * 1000 - elapsed);
      ringNoteTimers.push(setTimeout(() => showRingNote(text), delay));
    }
  }

  function clearRingNote() {
    for (const id of ringNoteTimers) clearTimeout(id);
    ringNoteTimers = [];
    el.ringNote.hidden = true;
  }

  /** The call screen as it looks the instant before a call connects. */
  function resetCallScreen() {
    el.phoneScreen.classList.remove("is-live");
    el.liveDisc.classList.remove("is-connected", "is-agent", "is-caller");
    el.liveDisc.classList.add("is-ringing");
    el.callState.hidden = false;
    setCallState("calling…");
    el.speakingLabel.textContent = "ringing";
    el.callTimer.hidden = true;
    el.callTimer.textContent = "00:00";
    el.islandTimer.textContent = "00:00";
    el.limitNote.hidden = true;
    clearRingNote();
    el.muteButton.setAttribute("aria-pressed", "false");
    el.muteLabel.textContent = "mute";
  }

  /* A handset does not offer to redial. It shows the call ended, then puts you back
   * where you were - so the ended screen clears itself rather than growing a button.
   * The write-up beside the phone stays where it is. */
  function returnToDialer() {
    clearTimeout(returnTimer);
    returnTimer = null;
    resetCallScreen();
    show("dialer");
  }

  function buildClient() {
    return new PipecatClient({
      transport: new DailyTransport({
        // Deliberately NOT bufferLocalAudioUntilBotReady. It sounds right - hold what
        // the caller says before the bot is ready - but the agent mutes the caller
        // until the greeting completes (MuteUntilFirstBotCompleteUserMuteStrategy in
        // services.build_user_mute_strategies), so the buffered audio is discarded
        // server-side anyway. Enabling it only puts a recorder and an AudioWorklet on
        // the microphone path, and its teardown double-frees on a failed join.
        //
        // Spread into Daily.createCallObject() and applied by startCamera(). Daily
        // replace their own constraints wholesale rather than merging, so these three
        // are stated explicitly and then read back in reportAudioConstraints().
        inputSettings: {
          audio: {
            settings: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      }),
      enableMic: true,
      enableCam: false,
      callbacks: {
        onBotReady: () => connected(),

        onUserStartedSpeaking: () => {
          el.liveDisc.classList.add("is-caller");
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "Listening";
        },
        onUserStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "On the call";
        },

        onBotStartedSpeaking: () => {
          // bot-ready is the correct signal, but relying on one signal for an audible
          // tone is how this broke before. If the agent is talking, we are connected.
          connected();
          el.liveDisc.classList.add("is-agent");
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "Speaking";
          agentBuffer = "";
          agentLive = caption("Hydrofoil Academy", "caption-agent");
        },
        onBotStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "On the call";
          agentLive = null;
        },

        onBotTtsText: (data) => {
          if (agentLive && data && data.text) {
            agentBuffer += (agentBuffer ? " " : "") + data.text;
            agentLive.textContent = agentBuffer;
            el.captions.scrollTop = el.captions.scrollHeight;
          }
        },
        onBotOutput: (data) => {
          // Fallback: only if nothing streamed for this turn.
          if (agentLive && !agentBuffer && data && data.text) {
            agentLive.textContent = data.text;
          }
        },

        onUserTranscript: (data) => {
          if (!data || !data.text) return;
          if (!callerLive) callerLive = caption("You", "caption-caller");
          callerLive.textContent = data.text;
          el.captions.scrollTop = el.captions.scrollHeight;
          if (data.final) callerLive = null;
        },

        // The read-out. dashboard.py publishes a snapshot after every funnel tool
        // call; the SDK hands us the payload directly.
        onServerMessage: (data) => {
          if (data && data.type === "call-state") renderReadout(data.state);
        },

        onTrackStarted: (track, participant) => {
          if (track.kind !== "audio") return;
          if (participant && participant.local) attachMicAnalyser(track);
          else attachBotAudio(track);
        },

        onDeviceError: (error) => {
          console.warn("device error", error);
          hangUp("This needs your microphone. Allow access and try again.");
        },

        // The bot hangs up by leaving the room. Ending on that, not on our own
        // connection dropping, is what stops the handset showing "on the call" to an
        // empty room while Pipecat Cloud tears it down.
        onBotDisconnected: () => hangUp(null),

        onDisconnected: () => hangUp(null),

        onError: (message) => console.warn("agent error", message),
        onMessageError: (message) => console.warn("agent message error", message),
      },
    });
  }

  /** What a mint that failed means. /api/session refuses nothing but the passcode. */
  function mintFailed({ status }) {
    if (status === 401 && config.passcodeRequired) {
      // The code was right when the visitor typed it; DEMO_PASSCODE has changed since.
      passcode = "";
      show("gate");
      fail(el.gateError, "That passcode is not right any more. Check the code you were sent.");
      el.passcode.value = "";
      el.passcode.focus();
      return;
    }
    // Unconfigured, unreachable, or a 500. Recoverable, and the button is right there:
    // pressing it again mints again, which is the whole of what this buys.
    show("dialer");
    fail(el.dialError, "Could not reach the demo server. Try again.");
  }

  /** Stop what the call screen started WITHOUT the ended screen: the caller is about to put
   *  a different one up, and hangUp() would schedule a return to the dialer over the top. */
  function abandonCall() {
    ended = true;
    clearTimeout(returnTimer);
    returnTimer = null;
    teardown();
    resetCallScreen();
  }

  async function placeCall() {
    isConnected = false;
    el.dialError.hidden = true;
    // Nothing should be callable before the gate is cleared, or the call fails at
    // the offer and the visitor gets an error instead of an explanation.
    if (config.passcodeRequired && !passcode) {
      show("gate");
      el.passcode.focus();
      return;
    }

    // BEFORE THE FIRST AWAIT, while this tap still counts (see openAudioContext). The
    // play() has no source yet; what it buys is the element's leave to play the bot's
    // track when that arrives, seconds from now. A context left open by a failed mint is
    // reused rather than leaked.
    if (!audioCtx) audioCtx = openAudioContext();
    el.remoteAudio.play().catch(() => {});

    // MINTED HERE, a moment before it is spent. A page that has been open since this
    // morning is the normal case for a demo link, not the exception.
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    ended = false;

    clearTimeout(returnTimer);
    returnTimer = null;
    show("call");
    resetCallScreen();
    callT0 = performance.now();
    scheduleRingNotes();
    el.captions.replaceChildren();
    el.readoutBody.replaceChildren();
    el.readoutBody.hidden = false;
    el.leadSheet.replaceChildren();
    el.cardWrap.hidden = true;
    startedAt = 0;
    renderStepper(null);

    // Start the agent. Pipecat Cloud creates the Daily room, puts the agent in it and
    // returns the room URL with a meeting token for us.
    let session;
    try {
      let res = await postJson("/api/connect", { token: minted.token });
      if (res.status === 401) {
        // The server refused a token this page thought was live: a browser clock further
        // behind than the margin, or a secret rotated under us. One re-mint and one retry,
        // because nobody should pay a reload for either. A second 401 is a real refusal.
        sessionToken.clear();
        const again = await sessionToken.get();
        if (!again.ok) {
          abandonCall();
          return mintFailed(again);
        }
        res = await postJson("/api/connect", { token: again.token });
      }
      if (res.status === 503) return hangUp("The demo line is busy. Try again shortly.");
      if (res.status === 401) return hangUp("Couldn't start the call. Try again.");
      if (!res.ok) return hangUp("Couldn't get through to the academy.");
      session = await res.json();
    } catch (err) {
      console.error(err);
      return hangUp("Couldn't get through to the academy.");
    }

    startRingback();
    setCallState("ringing…");

    client = buildClient();
    try {
      // DailyTransport normalises dailyRoom -> url and dailyToken -> token itself, so
      // the Pipecat Cloud response goes in unreshaped.
      await client.connect(session);
      reportAudioConstraints();
    } catch (err) {
      console.error(err);
      hangUp("Couldn't get through to the academy.");
    }
  }

  function connected() {
    if (isConnected) return;
    isConnected = true;
    stopRingback();
    clearRingNote();
    el.liveDisc.classList.remove("is-ringing");
    el.liveDisc.classList.add("is-connected");
    // Connected, so the handset drops the "calling" line and shows the clock -
    // in the status line and in the island, which widens to carry it.
    el.callState.hidden = true;
    el.callTimer.hidden = false;
    el.phoneScreen.classList.add("is-live");
    el.speakingLabel.textContent = "on the call";
    startedAt = Date.now();
    startWave();

    timerTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const mm = String(Math.floor(secs / 60)).padStart(2, "0");
      const ss = String(secs % 60).padStart(2, "0");
      const clock = `${mm}:${ss}`;
      el.callTimer.textContent = clock;
      el.islandTimer.textContent = clock;
      if (config.maxCallSeconds > 0) {
        const left = config.maxCallSeconds - secs;
        if (left <= 60 && left > 0) {
          el.limitNote.hidden = false;
          el.limitNote.textContent = `This demo call ends in ${left}s.`;
        }
      }
    }, 500);
  }

  function teardown() {
    clearInterval(timerTimer);
    timerTimer = null;
    stopRingback();
    clearRingNote();
    stopWave();
    if (client) {
      // Fire and forget: the page may be unloading, and there is nothing to wait for.
      client.disconnect().catch(() => {});
      client = null;
    }
    if (audioCtx) audioCtx.close().catch(() => {});
    el.remoteAudio.srcObject = null;
    audioCtx = remoteSource = remoteAnalyser = micSource = micAnalyser = null;
  }

  function hangUp(reason) {
    if (ended) return;
    ended = true;
    const secs = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    teardown();

    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    el.phoneScreen.classList.remove("is-live");
    el.endedEyebrow.textContent = secs ? `Call Ended · ${mm}:${ss}` : "Call Ended";
    el.endedTitle.textContent = "Hydrofoil Academy";
    el.endedSub.textContent = reason || "";

    // leadCard is only rendered once the call has closed, so its presence is the
    // signal that there is a lead worth showing at all.
    // The sheet says everything the live read-out said and more, so the read-out is
    // put away rather than left above it repeating itself down the page.
    if (lastState && lastState.leadCard) {
      renderLeadSheet(lastState);
      el.readoutBody.hidden = true;
      el.cardWrap.hidden = false;
    } else {
      el.cardWrap.hidden = true;
    }
    show("ended");
    // Long enough to read, short enough that nobody wonders what to do next.
    returnTimer = setTimeout(returnToDialer, reason ? 4000 : 2400);
  }

  /* ── wiring ─────────────────────────────────────────────────────── */

  el.callButton.addEventListener("click", placeCall);

  el.endButton.addEventListener("click", () => hangUp(null));

  el.muteButton.addEventListener("click", () => {
    if (!client) return;
    // The transport owns the track, so this goes through the client rather than
    // flipping track.enabled behind its back. aria-pressed means muted; the label is
    // the next action, not the current state.
    const wasEnabled = client.isMicEnabled;
    client.enableMic(!wasEnabled);
    el.muteButton.setAttribute("aria-pressed", String(wasEnabled));
    el.muteLabel.textContent = wasEnabled ? "unmute" : "mute";
  });

  el.copyButton.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText((lastState && lastState.leadCard) || "");
      el.copyButton.textContent = "Copied";
      setTimeout(() => (el.copyButton.textContent = "Copy"), 1600);
    } catch (_) {
      el.copyButton.textContent = "Press ⌘C";
    }
  });

  el.gateForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    el.gateError.hidden = true;
    // The gate IS a mint: /api/session is what checks the passcode, and what is kept
    // afterwards is the code as well as the token, so a call placed three hours from now
    // can mint again without asking the visitor for a number they no longer have in front
    // of them.
    passcode = el.passcode.value;
    const minted = await sessionToken.get();
    if (minted.ok) return show("dialer");

    passcode = "";
    fail(el.gateError, minted.status === 401
      ? "That passcode is not right."
      : "Could not reach the demo server. Try again.");
    el.passcode.select();
  });

  window.addEventListener("beforeunload", teardown);

  /* ── start ──────────────────────────────────────────────────────── */

  (async () => {
    // Show the stages up front, greyed. The panel then reads as something waiting to
    // be filled rather than as an empty box.
    renderStepper(null);
    try {
      config = await (await fetch("/api/config")).json();
    } catch (_) {
      fail(el.dialError, "Could not reach the demo server.");
    }
    if (config.passcodeRequired) {
      show("gate");
      el.passcode.focus();
    } else {
      // No gate, so there is nothing to ask for and nothing to mint yet: the token is
      // minted when the button is pressed.
      show("dialer");
    }
  })();
})();
